import { useEffect, useState } from "react";
import Select from "react-select";
import { XMarkIcon, PhotoIcon, PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import facilityService from "../../services/facilityService";
import { validateFacilityForm } from "../../utils/facilityValidators";
import { domainsStringToChips, chipsToDomainsString } from "../../utils/domains";
import { formatPhoneDisplay } from "../../utils/phoneMask";
import { COUNTRIES, TIMEZONE_OPTIONS } from "../../constants/facility.constants";
import { singleSelectStyles } from "../../../../helpers/constant";

// Precomputed once -- COUNTRIES is static, so there's no need to rebuild this
// {value,label} option list on every render.
const countryOptions = COUNTRIES.map((country) => ({ value: country.name, label: country.name }));

const emptyForm = {
  name: "",
  description: "",
  address: "",
  phone_number: "",
  country: "",
  timezone: "",
  referral_code: "",
  primary_domain: "",
  domains: "",
  intranet_allowed_domains: [""],
  api_rate_limit: "",
  web_search_limit: "30",
  intranet_search_limit: "50",
  project_limit: "10",
  collection_limit: "5",
  logo_urls: { light_theme_logo: "", dark_theme_logo: "", desktop_app_logo: "" },
};

const fieldClass = (hasError) =>
  `w-full rounded-lg border bg-gray-50 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 ${
    hasError ? "border-red-400 focus:ring-red-100" : "border-gray-200 focus:ring-blue-100 focus:border-blue-300"
  }`;

const FieldLabel = ({ children, required }) => (
  <label className="mb-1 block text-sm font-medium text-gray-700">
    {children}
    {required && <span className="text-red-500"> *</span>}
  </label>
);

// Small square preview for a pasted logo/icon URL. Falls back to a
// placeholder icon while the URL is empty or fails to load (e.g. broken link).
const LogoPreview = ({ url, background = "bg-gray-50" }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [url]);

  const showImage = url && !failed;

  return (
    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 ${background}`}>
      {showImage ? (
        <img src={url} alt="Logo preview" className="h-full w-full object-contain" onError={() => setFailed(true)} />
      ) : (
        <PhotoIcon className="h-5 w-5 text-gray-300" />
      )}
    </div>
  );
};

const SectionCard = ({ title, subtitle, children }) => (
  <div className="mb-6 rounded-xl border border-gray-100 p-5">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h3>
    {subtitle && <p className="mb-4 mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </div>
);

const FacilityDetailsTab = ({ facility, isNew, onSaved, onCancel }) => {
  const [form, setForm] = useState(emptyForm);
  const [domainChips, setDomainChips] = useState([]);
  const [newChip, setNewChip] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);

  const callingCode = COUNTRIES.find((country) => country.name === form.country)?.callingCode;

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setDomainChips([]);
      return;
    }

    setIsLoading(true);
    facilityService
      .getFacilityById(facility.id)
      .then((data) => {
        const logoUrls =
          data?.logo_urls && !Array.isArray(data.logo_urls)
            ? data.logo_urls
            : { light_theme_logo: "", dark_theme_logo: "", desktop_app_logo: "" };
        const intranetAllowedDomains =
          Array.isArray(data?.intranet_allowed_domains) && data.intranet_allowed_domains.length > 0
            ? data.intranet_allowed_domains
            : [""];

        setForm({
          ...emptyForm,
          ...data,
          logo_urls: logoUrls,
          intranet_allowed_domains: intranetAllowedDomains,
        });
        setDomainChips(domainsStringToChips(data?.domains));
      })
      .catch((error) => console.error("Error fetching facility details:", error))
      .finally(() => setIsLoading(false));
  }, [facility?.id, isNew]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAddChip = () => {
    const value = newChip.trim();
    if (!value) return;
    setDomainChips((prev) => [...prev, value]);
    setNewChip("");
  };

  const handleRemoveChip = (index) => {
    setDomainChips((prev) => prev.filter((_, i) => i !== index));
  };

  const handleIntranetDomainChange = (index, value) => {
    const updated = [...form.intranet_allowed_domains];
    updated[index] = value;
    handleChange("intranet_allowed_domains", updated);
  };

  const addIntranetDomain = () => {
    handleChange("intranet_allowed_domains", [...form.intranet_allowed_domains, ""]);
  };

  const removeIntranetDomain = (index) => {
    handleChange(
      "intranet_allowed_domains",
      form.intranet_allowed_domains.filter((_, i) => i !== index)
    );
  };

  const handleSave = async () => {
    const validationErrors = validateFacilityForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      ...form,
      domains: chipsToDomainsString(domainChips),
      web_search_limit: form.web_search_limit || 30,
      intranet_search_limit: form.intranet_search_limit || 50,
      project_limit: form.project_limit || 10,
      collection_limit: form.collection_limit || 5,
      intranet_allowed_domains: form.intranet_allowed_domains.filter((domain) => domain.trim() !== ""),
    };
    if (!payload.logo_urls.light_theme_logo && !payload.logo_urls.dark_theme_logo && !payload.logo_urls.desktop_app_logo) {
      delete payload.logo_urls;
    }

    setIsSaving(true);
    try {
      const saved = isNew
        ? await facilityService.createFacility(payload)
        : await facilityService.updateFacility(facility.id, payload);
      toast.success(isNew ? "Facility created successfully!" : "Facility updated successfully!");
      onSaved(saved);
    } catch (error) {
      console.error("Error saving facility:", error);
      toast.error("Failed to save facility. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Loading facility details...</div>;
  }

  return (
    <div>
      <SectionCard title="Facility details">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Name</FieldLabel>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Tulip Hospital"
              className={fieldClass(errors.name)}
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>
          <div>
            <FieldLabel required>Description</FieldLabel>
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="e.g. Regional clinic network"
              className={fieldClass(errors.description)}
            />
            {errors.description && <p className="mt-1 text-xs text-red-500">{errors.description}</p>}
          </div>

          <div className="col-span-2">
            <FieldLabel>Address</FieldLabel>
            <input
              type="text"
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              placeholder="Street, city, state, ZIP"
              className={fieldClass(false)}
            />
          </div>

          <div>
            <FieldLabel>Phone number</FieldLabel>
            <div
              className={`flex items-center rounded-lg border bg-gray-50 focus-within:ring-2 ${
                errors.phone_number
                  ? "border-red-400 focus-within:ring-red-100"
                  : "border-gray-200 focus-within:border-blue-300 focus-within:ring-blue-100"
              }`}
            >
              <span className="flex-shrink-0 border-r border-gray-200 px-3 py-2.5 text-sm text-gray-500">
                +{callingCode || "--"}
              </span>
              <input
                type="text"
                value={formatPhoneDisplay(form.phone_number)}
                placeholder="000 000 0000"
                onChange={(e) => handleChange("phone_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="w-full min-w-0 bg-transparent px-3 py-2.5 text-sm focus:outline-none"
              />
            </div>
            {errors.phone_number && <p className="mt-1 text-xs text-red-500">{errors.phone_number}</p>}
          </div>
          <div>
            <FieldLabel>Country</FieldLabel>
            <Select
              value={countryOptions.find((option) => option.value === form.country) || null}
              onChange={(option) => handleChange("country", option?.value || "")}
              options={countryOptions}
              placeholder="Select country"
              isClearable
              isSearchable
              styles={singleSelectStyles}
            />
          </div>

          <div>
            <FieldLabel required>Time zone</FieldLabel>
            <Select
              value={TIMEZONE_OPTIONS.find((option) => option.value === form.timezone) || null}
              onChange={(option) => handleChange("timezone", option?.value || "")}
              options={TIMEZONE_OPTIONS}
              placeholder="Select time zone"
              isClearable
              isSearchable
              styles={singleSelectStyles}
            />
            {errors.timezone && <p className="mt-1 text-xs text-red-500">{errors.timezone}</p>}
          </div>
          <div>
            <FieldLabel required>Referral code</FieldLabel>
            <input
              type="text"
              value={form.referral_code}
              placeholder="ABCD-2025"
              maxLength={9}
              onChange={(e) => {
                let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                if (value.length > 4) value = `${value.slice(0, 4)}-${value.slice(4)}`;
                handleChange("referral_code", value);
              }}
              className={fieldClass(errors.referral_code)}
            />
            {errors.referral_code && <p className="mt-1 text-xs text-red-500">{errors.referral_code}</p>}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Domains">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Primary domain</FieldLabel>
            <input
              type="text"
              value={form.primary_domain}
              onChange={(e) => handleChange("primary_domain", e.target.value)}
              placeholder="facility.com"
              className={fieldClass(false)}
            />
          </div>
          <div>
            <FieldLabel>Additional domains</FieldLabel>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
              {domainChips.map((chip, index) => (
                <span key={`${chip}-${index}`} className="inline-flex items-center gap-1 rounded-full bg-[#EEF0FB] px-2.5 py-1 text-xs font-medium text-[#2B3674]">
                  {chip}
                  <XMarkIcon className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveChip(index)} />
                </span>
              ))}
              <input
                type="text"
                value={newChip}
                onChange={(e) => setNewChip(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    handleAddChip();
                  }
                }}
                onBlur={handleAddChip}
                placeholder="facility.com"
                className="min-w-[80px] flex-1 bg-transparent px-1 py-1 text-sm focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <FieldLabel>Intranet allowed domains</FieldLabel>
            <span className="text-xs text-gray-400">Only these hosts can be crawled</span>
          </div>
          {form.intranet_allowed_domains.map((domain, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={domain}
                onChange={(e) => handleIntranetDomainChange(index, e.target.value)}
                placeholder="intranet.example.org"
                className={fieldClass(false)}
              />
              {form.intranet_allowed_domains.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeIntranetDomain(index)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100"
                >
                  <MinusIcon className="h-4 w-4" />
                </button>
              )}
              {index === form.intranet_allowed_domains.length - 1 && (
                <button
                  type="button"
                  onClick={addIntranetDomain}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#2B3674] text-white hover:bg-blue-800"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Limits & quotas" subtitle="Per facility, per day">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel required>API rate limit</FieldLabel>
            <input
              type="text"
              value={form.api_rate_limit}
              onChange={(e) => handleChange("api_rate_limit", e.target.value.replace(/\D/g, ""))}
              placeholder="Requests per minute"
              className={fieldClass(errors.api_rate_limit)}
            />
            {errors.api_rate_limit && <p className="mt-1 text-xs text-red-500">{errors.api_rate_limit}</p>}
          </div>
          <div>
            <FieldLabel>Websearch limit</FieldLabel>
            <input
              type="text"
              value={form.web_search_limit}
              onChange={(e) => handleChange("web_search_limit", e.target.value.replace(/\D/g, ""))}
              placeholder="Searches per user / day"
              className={fieldClass(errors.web_search_limit)}
            />
          </div>
          <div>
            <FieldLabel>Intranet search limit</FieldLabel>
            <input
              type="text"
              value={form.intranet_search_limit}
              onChange={(e) => handleChange("intranet_search_limit", e.target.value.replace(/\D/g, ""))}
              placeholder="Queries per user / day"
              className={fieldClass(errors.intranet_search_limit)}
            />
          </div>
          <div>
            <FieldLabel>Project limit</FieldLabel>
            <input
              type="text"
              value={form.project_limit}
              onChange={(e) => handleChange("project_limit", e.target.value.replace(/\D/g, ""))}
              placeholder="Active projects"
              className={fieldClass(errors.project_limit)}
            />
          </div>
          <div>
            <FieldLabel>Collection limit</FieldLabel>
            <input
              type="text"
              value={form.collection_limit}
              onChange={(e) => handleChange("collection_limit", e.target.value.replace(/\D/g, ""))}
              placeholder="Knowledge collections"
              className={fieldClass(errors.collection_limit)}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Branding">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Light theme logo</FieldLabel>
            <div className="flex items-center gap-3">
              <LogoPreview url={form.logo_urls.light_theme_logo} />
              <input
                type="text"
                value={form.logo_urls.light_theme_logo}
                onChange={(e) => setForm((prev) => ({ ...prev, logo_urls: { ...prev.logo_urls, light_theme_logo: e.target.value } }))}
                placeholder="Paste logo URL"
                className={fieldClass(false)}
              />
            </div>
          </div>
          <div>
            <FieldLabel>Dark theme logo</FieldLabel>
            <div className="flex items-center gap-3">
              <LogoPreview url={form.logo_urls.dark_theme_logo} background="bg-gray-900" />
              <input
                type="text"
                value={form.logo_urls.dark_theme_logo}
                onChange={(e) => setForm((prev) => ({ ...prev, logo_urls: { ...prev.logo_urls, dark_theme_logo: e.target.value } }))}
                placeholder="Paste logo URL"
                className={fieldClass(false)}
              />
            </div>
          </div>
          <div className="col-span-2">
            <FieldLabel>Icon</FieldLabel>
            <div className="flex items-center gap-3">
              <LogoPreview url={form.logo_urls.desktop_app_logo} />
              <input
                type="text"
                value={form.logo_urls.desktop_app_logo}
                onChange={(e) => setForm((prev) => ({ ...prev, logo_urls: { ...prev.logo_urls, desktop_app_logo: e.target.value } }))}
                placeholder="Paste icon URL (square, min 128x128)"
                className={fieldClass(false)}
              />
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
        <button type="button" onClick={onCancel} className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-xl bg-[#2B3674] px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {isSaving ? "Saving..." : isNew ? "Create facility" : "Save details"}
        </button>
      </div>
    </div>
  );
};

export default FacilityDetailsTab;
